import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Mail, User, Phone, FileText, Plus, X, ExternalLink } from 'lucide-react';
import { authAPI } from '../../services/api';
import PasswordInput from '../PasswordInput';
import { validatePasswordStrength } from '../../utils/passwordValidator';
import { useTranslation } from 'react-i18next';

interface SignupFormProps {
  onSuccess?: () => void;
}

const SignupForm = ({ onSuccess }: SignupFormProps) => {
  const { register, loading, error } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
    bio: ''
  });
  const [socialMediaLinks, setSocialMediaLinks] = useState<Array<{ platform: string; url: string }>>([]);
  const [oauthState, setOauthState] = useState({
    googleReady: false,
    scriptsLoaded: false
  });
  const [initializationAttempted, setInitializationAttempted] = useState(false);

  // Google Sign-In configuration
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Function to handle credential response
  const handleCredentialResponse = async (response: any) => {
    try {
      // The 'response.credential' is the JWT ID token.
      const id_token = response.credential;

      // Send this token to your back-end for verification using the API service.
      const data = await authAPI.googleLogin(id_token);
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        window.location.href = '/dashboard';
      }
      onSuccess?.();
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  useEffect(() => {
    // Prevent multiple initialization attempts
    if (initializationAttempted) {
      return;
    }
    setInitializationAttempted(true);

    // Set initial state to indicate scripts are being loaded
    setOauthState(prev => ({ ...prev, scriptsLoaded: true }));

    // Set up Google Sign-In when script is ready
    const initializeGoogleSignIn = () => {
      try {
        if (!googleClientId || !((window as any).google && (window as any).google.accounts && (window as any).google.accounts.id)) {
          return;
        }
        
        // Set up global callback function
        (window as any).handleCredentialResponse = handleCredentialResponse;

        // Initialize Google Sign-In
        (window as any).google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
        });

        // Mark as ready to render button
        setOauthState(prev => ({ ...prev, googleReady: true }));

        // Render the button after a short delay to ensure DOM is ready
        setTimeout(() => {
          const buttonContainer = document.getElementById('google-signup-button');
          
          if (buttonContainer) {
            try {
              (window as any).google.accounts.id.renderButton(
                buttonContainer,
                {
                  type: 'standard',
                  size: 'large',
                  theme: 'outline',
                  text: 'signup_with',
                  shape: 'rectangular',
                  logo_alignment: 'left',
                }
              );
            } catch (renderError) {
              console.error('Error rendering Google Sign-In button:', renderError);
            }
          }
        }, 100);

      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
      }
    };

    // Use consolidated initialization approach
    const initializeOAuth = () => {
      if (googleClientId) {
        // Use the global helper to wait for Google script
        if ((window as any).onGoogleReady) {
          (window as any).onGoogleReady(initializeGoogleSignIn);
        } else {
          // Fallback if the helper isn't available
          const checkGoogleScript = () => {
            if (typeof (window as any).google !== 'undefined' && 
                typeof (window as any).google.accounts !== 'undefined' && 
                typeof (window as any).google.accounts.id !== 'undefined') {
              initializeGoogleSignIn();
            } else {
              setTimeout(checkGoogleScript, 100);
            }
          };
          checkGoogleScript();
        }

        // Also try direct initialization if Google is already available
        if (typeof (window as any).google !== 'undefined' && 
            typeof (window as any).google.accounts !== 'undefined' && 
            typeof (window as any).google.accounts.id !== 'undefined') {
          setTimeout(() => initializeGoogleSignIn(), 500);
        }
      }
    };

    // Initialize OAuth only if we have at least one client ID
    if (googleClientId) {
      initializeOAuth();
    }
  }, [googleClientId, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Validate password strength before submission
    const passwordValidation = validatePasswordStrength(formData.password);
    if (!passwordValidation.valid) {
      alert('Password does not meet the strength requirements. Please choose a stronger password.');
      return;
    }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: formData.displayName || undefined,
        phone: formData.phone || undefined,
        bio: formData.bio || undefined,
        socialMediaLinks: socialMediaLinks.length > 0 ? socialMediaLinks : undefined
      });
      onSuccess?.();
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const addSocialMediaLink = () => {
    setSocialMediaLinks([...socialMediaLinks, { platform: '', url: '' }]);
  };

  const removeSocialMediaLink = (index: number) => {
    setSocialMediaLinks(socialMediaLinks.filter((_, i) => i !== index));
  };

  const updateSocialMediaLink = (index: number, field: 'platform' | 'url', value: string) => {
    const updated = [...socialMediaLinks];
    updated[index] = { ...updated[index], [field]: value };
    setSocialMediaLinks(updated);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {t('auth.signUpTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
            {t('auth.haveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('header.signIn')}
            </Link>
          </p>
          <div className="mt-4 rounded-md bg-blue-50 dark:bg-blue-900/20 p-4">
            <div className="text-sm text-blue-800 dark:text-blue-200 text-center">
              <strong>Just browsing?</strong> No account needed to blip tours and travel data. 
              Accounts are only required for content creators who want to create and manage tours.
            </div>
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="sr-only">
                  {t('auth.firstName')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                    placeholder={t('auth.firstName')}
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="lastName" className="sr-only">
                  {t('auth.lastName')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                    placeholder={t('auth.lastName')}
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="displayName" className="sr-only">
                {t('auth.displayName')} ({t('common.optional')})
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  autoComplete="nickname"
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                  placeholder={`${t('auth.displayName')} (${t('common.optional')})`}
                  value={formData.displayName}
                  onChange={handleInputChange}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                This name will be shown publicly when you create content
              </p>
            </div>

            <div>
              <label htmlFor="email" className="sr-only">
                {t('auth.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                  placeholder={t('auth.email')}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="sr-only">
                {t('auth.phoneNumber')} ({t('common.optional')})
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                  placeholder={`${t('auth.phoneNumber')} (${t('common.optional')})`}
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="bio" className="sr-only">
                {t('auth.bio')} ({t('common.optional')})
              </label>
              <div className="relative">
                <div className="absolute top-3 left-0 pl-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <textarea
                  id="bio"
                  name="bio"
                  rows={3}
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm resize-none"
                  placeholder={t('auth.tellAboutYourself')}
                  value={formData.bio}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Social Media Links Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.socialLinks')} ({t('common.optional')})
                </label>
                <button
                  type="button"
                  onClick={addSocialMediaLink}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-primary-600 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {t('auth.addSocialLink')}
                </button>
              </div>
              {socialMediaLinks.map((link, index) => (
                <div key={index} className="flex space-x-2 mb-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="text"
                      placeholder={t('auth.platformPlaceholder')}
                      value={link.platform}
                      onChange={(e) => updateSocialMediaLink(index, 'platform', e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 sm:text-sm"
                    />
                  </div>
                  <div className="flex-2">
                    <input
                      type="url"
                      placeholder={t('auth.urlPlaceholder')}
                      value={link.url}
                      onChange={(e) => updateSocialMediaLink(index, 'url', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 sm:text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSocialMediaLink(index)}
                    className="inline-flex items-center px-2 py-2 border border-red-200 rounded-md text-red-600 bg-red-50 hover:bg-red-100"
                    aria-label={t('auth.removeSocialLink')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('auth.password')}
                label={t('auth.password')}
                autoComplete="new-password"
                required
                showStrengthIndicator={true}
              />
            </div>

            <div>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('auth.confirmPassword')}
                label={t('auth.confirmPassword')}
                autoComplete="new-password"
                required
                showStrengthIndicator={false}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.signingUp') : t('header.signUp')}
            </button>
          </div>
        </form>

        {/* OAuth Sign-In Section - Always render container to prevent layout shifts */}
        {googleClientId && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 dark:text-gray-500">{t('auth.orContinueWith')}</span>
              </div>
            </div>

            {/* Google Sign-In */}
            {googleClientId && (
              <div className="mt-6">
                {oauthState.googleReady ? (
                  <div className="flex justify-center">
                    <div 
                      id="g_id_onload"
                      data-client_id={googleClientId}
                      data-callback="handleCredentialResponse"
                    ></div>
                    <div 
                      id="google-signup-button"
                      className="flex justify-center"
                      style={{ minHeight: '40px', minWidth: '240px' }}
                    ></div>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <div 
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 dark:text-gray-500 flex items-center justify-center"
                      style={{ height: '40px', width: '240px' }}
                    >
                      <span className="text-sm">Loading Google Sign-In...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupForm;